import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { appConfig } from "@/config/app.config";
import { showToast } from "@/lib/toast";
import {
  DEFAULT_ERROR_TITLE,
  extractServerError,
  getResponseStatus,
} from "./server-error";
import type { LoginResponse } from "./types";

let authToken: string | null = null;
let storedRefreshToken: string | null = null;
let onUnauthorized: (() => void) | null = null;
let onTokenRefreshed: ((loginResponse: LoginResponse) => void) | null = null;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function setRefreshToken(token: string | null): void {
  storedRefreshToken = token;
}

export function setOnUnauthorized(callback: () => void): void {
  onUnauthorized = callback;
}

export function setOnTokenRefreshed(
  callback: (loginResponse: LoginResponse) => void,
): void {
  onTokenRefreshed = callback;
}

function processQueue(error: unknown, token: string | null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
}

// Separate client for refresh requests — bypasses the auth interceptors
// to avoid infinite loops and circular dependency with api/auth.ts
const refreshClient = axios.create({
  baseURL: appConfig.api.baseUrl,
  timeout: appConfig.api.timeout,
  headers: { "Content-Type": "application/json" },
});

export async function refreshAccessToken(
  refreshToken: string,
): Promise<LoginResponse> {
  const response = await refreshClient.post<LoginResponse>(
    "/security/refreshToken",
    refreshToken,
    { headers: { "Content-Type": "text/plain" } },
  );
  return response.data;
}

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: appConfig.api.baseUrl,
    timeout: appConfig.api.timeout,
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });

  client.interceptors.request.use((config) => {
    // Only inject the Bearer token when no Authorization header is already set.
    // This preserves per-request overrides such as the Basic auth used by login.
    if (authToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Global handler: surface a single error toast for any 500 response,
      // then let the error propagate so callers' control flow is unaffected.
      if (getResponseStatus(error) === 500) {
        const serverError = extractServerError(error);
        showToast({
          variant: "error",
          title: serverError?.call ?? DEFAULT_ERROR_TITLE,
          ...(serverError?.message !== undefined && {
            description: serverError.message,
          }),
        });
        return Promise.reject(error);
      }

      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      if (!storedRefreshToken) {
        onUnauthorized?.();
        return Promise.reject(error);
      }

      // Queue concurrent requests while a refresh is already in progress
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshed = await refreshAccessToken(storedRefreshToken);
        authToken = refreshed.accessToken;
        storedRefreshToken = refreshed.refreshToken;
        onTokenRefreshed?.(refreshed);
        processQueue(null, refreshed.accessToken);
        originalRequest.headers.Authorization = `Bearer ${refreshed.accessToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        onUnauthorized?.();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    },
  );

  return client;
}

export const apiClient = createApiClient();
